import {useEffect, useState} from "react";
import {useParams} from "react-router";
import {
    addInventoryToCart,
    getPurchaseOrder,
    getPurchaseOrderLines,
    insertPurchaseOrderLineItem,
    createInvoice,
    deletePurchaseOrderLineItem,
    getPurchaseOrderTask,
    insertPurchaseOrderTask, getBuyer
} from "../../api/Api";
import {Button, Card, Collapse, Divider, Row, Col, Input, Popover, message} from "antd";
import {toUtcDateStringShort} from "../../util/timeutils";
import {PurchaseOrderStateWidget} from "../sales/PurchaseOrderStateWidget";
import {PurchaseOrderLineItemTable} from "../sales/PurchaseOrderLineItemTable";
import {ProductQuantitySelectWidget} from "../inventory/ProductQuantitySelectWidget";
import {isFulfillment} from "../../auth/sessionTools";
import {PurchaseOrderStatusType} from "../../api/Constants";
import {SimpleTaskTable} from "../sales/SimpleTaskTable";
import {NewTask} from "../tasks/NewTask";
import {BuyerInvoiceProfile} from "../sales/BuyerInvoiceProfile";

const {TextArea} = Input;

export const PurchaseOrderView = ({session, onApiFail, refresh, onRefresh, props}) => {
    const [purchaseOrder, setPurchaseOrder] = useState(undefined);
    const [poLines, setPoLines] = useState([]);
    const [poAssigns, setPoAssigns] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [product, setProduct] = useState(undefined);
    const [quantity, setQuantity] = useState(undefined);
    const [messageApi, contextHolder] = message.useMessage();
    const [invoiceCreated, setInvoiceCreated] = useState(false);
    const [buyer, setBuyer] = useState(undefined);

    const params = useParams();
    const purchaseId = params.purchaseId;

    const isFulfillmentDepartment = isFulfillment(session);


    useEffect(() => {
        if (purchaseId === undefined) {
            return;
        }
        getPurchaseOrder(purchaseId).then(result => {
            setPurchaseOrder(result);

            if (result !== undefined) {
                return getPurchaseOrderLines(result.id);
            } else {
                return Promise.resolve([]);
            }
        }).then(data => {
            const lines = data.map(l => {
                return {...l, key: l.id}
            })
            setPoLines(lines);
            const assigns = data.map(l => {
                if (l.inventoryId !== null) {
                    return l.inventoryId
                } else {
                    return undefined;
                }
            })
            setPoAssigns(assigns);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        getPurchaseOrderTask(purchaseId).then(r => {
            const taskData = r.map(row => {
                return {
                    ...row,
                    key: row.id
                }
            });
            setTasks(taskData);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [purchaseId, refresh, onApiFail]);

    useEffect(() => {
        if (purchaseOrder === undefined) {
            return;
        }
        getBuyer(purchaseOrder.buyerId).then(b => {
            setBuyer(b);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [purchaseOrder, onApiFail])

    const onSaveLineItem = () => {
        console.log(`save the item`);
        insertPurchaseOrderLineItem(purchaseId, {...product, quantity}).then(r => {
            if (onRefresh) onRefresh();
            setProduct(undefined);
            setQuantity(undefined);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onSelectAssign = (val, index) => {
        const nextAssigns = [...poAssigns];
        // user has picked this inventory
        nextAssigns[index] = val.id;
        setPoAssigns(nextAssigns);
    }

    const onCreateInvoice = () => {
        console.log(`create invoice`)
        // we need to test poLines against poAssigns
        let unAssignedError = false;
        poLines.forEach((l, index) => {
            if (poAssigns[index] === undefined ) {
                unAssignedError = true;
                console.log(`line at index ${index} is unassigned`);
            }
        });
        if (unAssignedError === true) {
            // some line items are unassigned so we can't proceed
            return;
        }

        // if we are here, everything is assigned, so we'll try to reserve everything to the cart
        const promises = [];
        poLines.forEach((l, index) => {
            promises.push(addInventoryToCart(poAssigns[index], l.quantity));
        });

        Promise.all(promises).then(r => {
            console.log(`creating invoice`);
            // invoice items is an object
            const invoiceItems = {};
            poLines.forEach((l, index) => {
                const inventoryId = poAssigns[index];
                const quantity = poLines[index].quantity;
                invoiceItems[inventoryId] = {
                    quantity,
                    item: {
                        id: inventoryId,
                        discount: 0,
                        lineDiscount: 0,
                        invoicePrice: 0,
                        linePrice: 0,
                    }
                }
            })
            return createInvoice(purchaseOrder.buyerId, invoiceItems);
        }).then(r => {
            setInvoiceCreated(true);
            messageApi.open({
                type: 'success',
                content: 'Invoice Created'
            });
        }).catch (e => {
            console.log(`create invoice failed ${e}`);
            messageApi.open({
                type: 'error',
                content: 'Failed to Create Invoice'
            });
        });
    }

    const onLineItemDelete = (index, item) => {
        deletePurchaseOrderLineItem(purchaseId, item.id).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onNewTask = (task) => {
        const newTask = [{
            ...task,
            id: task.id
        }]
        insertPurchaseOrderTask(purchaseId, newTask).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const collapseItem = [
        {
            key: '1',
            label: <div><Popover content={`Add More Products to this Purchase Order`}><b>Add More Products</b></Popover></div>,
            children: <ProductQuantitySelectWidget product={product} onProductSelect={setProduct} quantity={quantity} onQuantityChange={setQuantity}
                                                   onSave={onSaveLineItem} refresh={refresh} />
        }
    ];

    const invoiceDisable = invoiceCreated || poAssigns.reduce((acc, val) => {
        return acc || (val === undefined);
    }, false);

    if (!purchaseOrder) {
        return <div />
    }

    const purchaseOrderView =  <div style={{textAlign: 'left'}}>
        {contextHolder}
        <Card title={"Purchase Order"}>
            <Row style={{}}>
                <Col style={{width: 125}}><b>
                    Purchase Order:
                </b>
                </Col>
                <Col>{purchaseOrder.poNumber}</Col>
            </Row>
            <Row>
                <PurchaseOrderStateWidget purchaseOrder={purchaseOrder} onRefresh={onRefresh} onApiFail={onApiFail} isFulfillment={isFulfillmentDepartment}/>
            </Row>
        </Card>
        {buyer !== undefined &&
            <BuyerInvoiceProfile buyer={buyer} />
        }
        <Card title={'Due Date'}>
            <Row>
                <div>{toUtcDateStringShort(new Date(purchaseOrder.dueDate))}</div>
            </Row>
        </Card><p/>
        <Card title={'Notes'}>
            <TextArea maxLength={512} rows={4} showCount value={purchaseOrder.notes} />
        </Card>
        <Card title={'Tasks'}>
            {tasks.length > 0 &&
                <SimpleTaskTable tasks={tasks} enableLinks={true} />
            }
            <Collapse ghost bordered={false} items={[
                {
                    key: 1,
                    label: <b>Add Tasks</b>,
                    children: <NewTask onApiFail={onApiFail} refresh={refresh} onRefresh={onRefresh} onNewTask={onNewTask} />
                }
            ]}/>
        </Card>
        <Card title={'P.O. Items'}>
            <PurchaseOrderLineItemTable purchaseId={purchaseOrder.id} products={poLines} onSelectAssign={onSelectAssign}
                    onDelete={onLineItemDelete}
                    onApiFail={onApiFail} refresh={refresh} onRefresh={onRefresh} isFulfillment={isFulfillmentDepartment} showLink={true} showState={true} />
            {((purchaseOrder.state <= PurchaseOrderStatusType.Complete)) &&
                <div>
                    <Divider />
                    <Collapse bordered={false} ghost items={collapseItem} />
                </div>
            }
        </Card>
        <Card title={`Invoice Creation`}>
            <Button onClick={onCreateInvoice} disabled={invoiceDisable}>Create Invoice</Button>
        </Card>
    </div>

    return purchaseOrderView;
}